/**
 * User Email API Service
 * 
 * Handles API calls for per-user email address management.
 * Each user gets a unique email like john-028a@heyward.ai for deal tracking.
 * (Legacy *@iautom8.ai addresses still resolve via domain-blind matcher.)
 */

import { authFetch } from '../utils/authFetch';

const API_BASE = import.meta.env.REACT_APP_API_URL || window.location.origin;

/**
 * Get or create a user's unique email address
 * 
 * @param {string} userId - Clerk user ID
 * @param {string} firstname - User's first name for email generation
 * @returns {Promise<{email: Object, is_new: boolean}>}
 */
export const getOrCreateUserEmail = async (userId, firstname = 'user') => {
  const response = await authFetch(`${API_BASE}/api/v1/user-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user_id: userId,
      firstname: firstname,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to get/create user email: ${response.status}`);
  }

  return response.json();
};

/**
 * Get a user's existing email address
 * 
 * @param {string} userId - Clerk user ID
 * @returns {Promise<Object>} User email object
 */
export const getUserEmail = async (userId) => {
  const response = await authFetch(`${API_BASE}/api/v1/user-email/${userId}`);

  if (!response.ok) {
    if (response.status === 404) {
      return null; // User doesn't have an email yet
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to get user email: ${response.status}`);
  }

  return response.json();
};

/**
 * Get email statistics for a user
 * 
 * @param {string} userId - Clerk user ID
 * @param {number} recentLogsLimit - Max number of recent logs to include
 * @returns {Promise<Object>} User email stats
 */
export const getUserEmailStats = async (userId, recentLogsLimit = 10) => {
  const response = await authFetch(
    `${API_BASE}/api/v1/user-email/${userId}/stats?recent_logs_limit=${recentLogsLimit}`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to get email stats: ${response.status}`);
  }

  return response.json();
};

/**
 * Get email ingest logs for a user
 * 
 * @param {string} userId - Clerk user ID
 * @param {number} limit - Max number of logs to return
 * @returns {Promise<Array>} List of email ingest logs
 */
export const getUserEmailLogs = async (userId, limit = 20) => {
  const response = await authFetch(
    `${API_BASE}/api/v1/user-email/${userId}/logs?limit=${limit}`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to get email logs: ${response.status}`);
  }

  return response.json();
};

/**
 * Deactivate a user's email address
 * 
 * @param {string} userId - Clerk user ID
 * @returns {Promise<Object>} Success response
 */
export const deactivateUserEmail = async (userId) => {
  const response = await authFetch(`${API_BASE}/api/v1/user-email/${userId}/deactivate`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to deactivate email: ${response.status}`);
  }

  return response.json();
};

/**
 * Reactivate a user's email address
 * 
 * @param {string} userId - Clerk user ID
 * @returns {Promise<Object>} Success response
 */
export const reactivateUserEmail = async (userId) => {
  const response = await authFetch(`${API_BASE}/api/v1/user-email/${userId}/reactivate`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Failed to reactivate email: ${response.status}`);
  }

  return response.json();
};

export default {
  getOrCreateUserEmail,
  getUserEmail,
  getUserEmailStats,
  getUserEmailLogs,
  deactivateUserEmail,
  reactivateUserEmail,
};

